import { NgModule } from '@angular/core';
import { Routes, RouterModule, UrlSegment, Route } from '@angular/router';
import { AuthGuard } from 'src/app/auth/auth.guard';
import { NoAuthGuard } from 'src/app/auth/no-auth.guard';

import { ecommerceConstants } from 'src/app/shared/ecommerce-constants';
import { Country } from 'src/app/domain/Country';
import { CountryService } from 'src/app/services/country.service';

/**
 * Matcher para validar códigos de país en las rutas
 * Usa un cache estático de CountryService actualizado en APP_INITIALIZER
 */
/**
 * Matcher para validar códigos de país en las rutas
 * Usa un cache estático de CountryService (pre-seeded con todos los códigos conocidos)
 */
export function countryCodeMatcher(url: UrlSegment[]) {
  return CountryService.hasCountryCodeCached(url[0].path) ? { consumed: [url[0]] } : null;
}

/**
 * Lista estática de todos los países y sus locales.
 * Se usa para pre-generar las rutas sin depender de la API.
 */
const ALL_COUNTRIES: Pick<Country, 'code' | 'locale'>[] = [
  { code: 'ES', locale: 'es' },
  { code: 'IT', locale: 'it' },
  { code: 'PT', locale: 'pt' },
  { code: 'PL', locale: 'pl' },
  { code: 'RO', locale: 'ro' },
  { code: 'HR', locale: 'hr' },
  { code: 'GE', locale: 'ka' },
  { code: 'KZ', locale: 'ru' },
  { code: 'KG', locale: 'ru' },
  { code: 'MA', locale: 'fr' },
  { code: 'CI', locale: 'fr' },
  { code: 'UA', locale: 'uk' },
  { code: 'RS', locale: 'sr' },
  { code: 'SI', locale: 'sl' },
  { code: 'BG', locale: 'bg' },
  { code: 'BA', locale: 'bs' },
  { code: 'ME', locale: 'cnr' },
  { code: 'AD', locale: 'ca' },
  { code: 'AM', locale: 'hy' },
  { code: 'KE', locale: 'en' },
  { code: 'GH', locale: 'en' },
  { code: 'MD', locale: 'ro' },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/static/country-selection/country-selection.component').then(
        (m) => m.CountrySelectionComponent
      ),
  },
];

routes.push(
  {
    path: 'banner/:id',
    loadComponent: () =>
      import('./pages/static/preview-banner/preview-banner.component').then((m) => m.PreviewBannerComponent),
  },
  {
    path: 'urls-translated',
    loadComponent: () =>
      import('./pages/static/urls-translated/urls-translated.component').then((m) => m.UrlsTranslatedComponent),
  },
  {
    path: 'error-404',
    loadComponent: () => import('./pages/static/error/error.component').then((m) => m.ErrorComponent),
  },
  {
    matcher: countryCodeMatcher,
    children: generateCountryRoutes(ALL_COUNTRIES),
  },
  { path: '**', redirectTo: 'error-404', pathMatch: 'full' }
);

export function generateCountryRoutes(countries: Country[]): Route[] {
  const countryRoutes: Route[] = [];

  countries.forEach((country) => {
    countryRoutes.push({
      path: country.locale,
      data: { countryCode: country.code, locale: country.locale },
      children: [
      {
        path: ecommerceConstants.urls.home[country.code],
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls.login[country.code],
        loadComponent: () => import('./pages/user/login/login.component').then((m) => m.LoginComponent),
        canActivate: [NoAuthGuard],
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls['auto-login'][country.code] + '/:hash',
        loadComponent: () => import('./pages/user/auto-login/auto-login.component').then((m) => m.AutoLoginComponent)
      },
      {
        path: ecommerceConstants.urls.guest[country.code],
        loadComponent: () => import('./pages/user/guest/guest.component').then((m) => m.GuestComponent),
        canActivate: [NoAuthGuard],
      },
      {
        path: ecommerceConstants.urls['recover-password'][country.code],
        loadComponent: () =>
          import('./pages/user/recover-password/recover-password.component').then((m) => m.RecoverPasswordComponent),
      },
      {
        path: 'recover-password-confirmation/:hash',
        loadComponent: () =>
          import('./pages/user/recover-password-check/recover-password-check.component').then(
            (m) => m.RecoverPasswordCheckComponent
          ),
        canActivate: [NoAuthGuard],
      },
      {
        path: ecommerceConstants.urls['recover-password'][country.code] + '/' + ecommerceConstants.urls.error[country.code],
        loadComponent: () =>
          import('./pages/user/recover-password-error/recover-password-error.component').then(
            (m) => m.RecoverPasswordErrorComponent
          ),
        canActivate: [NoAuthGuard],
      },
      {
        path: ecommerceConstants.urls.register[country.code],
        loadComponent: () => import('./pages/user/register/register.component').then((m) => m.RegisterComponent),
        canActivate: [NoAuthGuard],
      },
      {
        path: ecommerceConstants.urls.activation[country.code] + '/:hash',
        loadComponent: () =>
          import('./pages/user/customer-activation/customer-activation.component').then(
            (m) => m.CustomerActivationComponent
          ),
      },
      {
        path: ecommerceConstants.urls['product-catalog'][country.code],
        loadComponent: () =>
          import('./pages/product-catalog/product-catalog.component').then((m) => m.ProductCatalogComponent),
      },
      {
        path: ecommerceConstants.urls.category[country.code] + '/:category',
        loadComponent: () => import('./pages/products/products.component').then((m) => m.ProductsComponent),
        data: { page: 'category' },
      },
      {
        path: ecommerceConstants.urls.collection[country.code] + '/:collection',
        loadComponent: () => import('./pages/products/products.component').then((m) => m.ProductsComponent),
        data: { page: 'collection' },
      },
      {
        path: ecommerceConstants.urls.products[country.code] + '/' + ecommerceConstants.urls['buy-again'][country.code],
        loadComponent: () => import('./pages/products/products.component').then((m) => m.ProductsComponent),
        canActivate: [AuthGuard],
        data: { page: 'buy-again' },
      },
      {
        path: ecommerceConstants.urls.products[country.code] + '/' + ecommerceConstants.urls['featured-products'][country.code],
        loadComponent: () => import('./pages/products/products.component').then((m) => m.ProductsComponent),
        data: { page: 'featured-products' },
      },
      {
        path: ecommerceConstants.urls.pack[country.code] + '/:pack',
        loadComponent: () => import('./pages/pack/pack.component').then((m) => m.PackComponent),
      },
      {
        path: ecommerceConstants.urls.product[country.code] + '/pvt/:product',
        data: { private: true },
        loadComponent: () => import('./pages/product/product.component').then((m) => m.ProductComponent),
      },
      {
        path: ecommerceConstants.urls.product[country.code] + '/:product',
        data: { private: false, hotjar: true },
        loadComponent: () => import('./pages/product/product.component').then((m) => m.ProductComponent),
      },
      {
        path: ecommerceConstants.urls['favourite-products'][country.code],
        loadComponent: () =>
          import('./pages/favourite-products/favourite-products.component').then(
            (m) => m.FavouriteProductsComponent
          ),
      },
      {
        path: ecommerceConstants.urls['search-results'][country.code],
        loadComponent: () =>
          import('./pages/search-results/search-results.component').then((m) => m.SearchResultsComponent),
      },
      {
        path: ecommerceConstants.urls.checkout[country.code],
        loadComponent: () => import('./pages/order/checkout/checkout.component').then((m) => m.CheckoutComponent),
        canActivate: [AuthGuard],
        data: { hotjar: true, sessionRecording: true },
      },
      {
        path: ecommerceConstants.urls['guest-checkout'][country.code],
        data: { hotjar: true, sessionRecording: true },
        loadComponent: () =>
          import('./pages/order/guest-checkout/guest-checkout.component').then((m) => m.GuestCheckoutComponent),
      },
      {
        path: ecommerceConstants.urls['order-thank-you'][country.code] + '/:orderId',
        loadComponent: () => import('./pages/order/thank-you/thank-you.component').then((m) => m.ThankYouComponent),
        data: { sessionRecording: true },
      },
      {
        path: ecommerceConstants.urls['my-account'][country.code],
        loadComponent: () => import('./pages/user/my-account/my-account.component').then((m) => m.MyAccountComponent),
        canActivate: [AuthGuard],
      },
      {
        path: ecommerceConstants.urls['my-orders'][country.code],
        loadComponent: () => import('./pages/user/my-orders/my-orders.component').then((m) => m.MyOrdersComponent),
        canActivate: [AuthGuard],
      },
      {
        path: ecommerceConstants.urls['glovo-wallet'][country.code],
        loadComponent: () => import('./pages/user/glovo-wallet/glovo-wallet.component').then((m) => m.GlovoWalletComponent),
        canActivate: [AuthGuard],
      },
      {
        path: ecommerceConstants.urls['glovo-bags'][country.code],
        loadComponent: () => import('./pages/user/glovo-bags/glovo-bags.component').then((m) => m.GlovoBagsComponent),
        canActivate: [AuthGuard],
      },
      {
        path: ecommerceConstants.urls['my-invoices'][country.code],
        loadComponent: () =>
          import('./pages/user/my-invoices/my-invoices.component').then((m) => m.MyInvoicesComponent),
        canActivate: [AuthGuard],
      },
      {
        path: ecommerceConstants.urls.services[country.code],
        loadComponent: () => import('./pages/services/services.component').then((m) => m.ServicesComponent),
      },
      {
        path: ecommerceConstants.urls['terms-and-conditions'][country.code],
        loadComponent: () => import('./pages/text-legal/text-legal.component').then((m) => m.TextLegalComponent),
        data: { page: 'terms-and-conditions' },
      },
      {
        path: ecommerceConstants.urls['return-policy'][country.code],
        loadComponent: () => import('./pages/text-legal/text-legal.component').then((m) => m.TextLegalComponent),
        data: { page: 'return-policy' },
      },
      {
        path: ecommerceConstants.urls['privacy-policy'][country.code],
        loadComponent: () => import('./pages/text-legal/text-legal.component').then((m) => m.TextLegalComponent),
        data: { page: 'privacy-policy' },
      },
      // Brands
      {
        path: ecommerceConstants.urls.landings[country.code] + '/' + ecommerceConstants.urls.brands[country.code],
        loadComponent: () => import('./pages/services/brands/brands.component').then((m) => m.BrandsComponent),
      },
      // Tracking
      {
        path: ecommerceConstants.urls.tracking[country.code],
        loadComponent: () => import('./pages/tracking/tracking-login/tracking-login.component').then((m) => m.TrackingLoginComponent),
      },
      {
        path: ecommerceConstants.urls.tracking[country.code] + '/' + ecommerceConstants.urls.products[country.code]  + '/:channelOrderNumber',
        loadComponent: () => import('./pages/tracking/tracking-detail/tracking-detail.component').then((m) => m.TrackingDetailComponent),
      },
      {
        path: ecommerceConstants.urls.tracking[country.code] + '/' + ecommerceConstants.urls['not-available'][country.code]  + '/:channelOrderNumber',
        loadComponent: () => import('./pages/tracking/tracking-not-available/tracking-not-available.component').then((m) => m.TrackingNotAvailableComponent),
      },
      // Refunds
      {
        path: ecommerceConstants.urls.refunds[country.code] + '/' + ecommerceConstants.urls.cancel[country.code] + '/' + ecommerceConstants.urls.contact[country.code] + '/:channelOrderNumber/:productSku',
        loadComponent: () =>
          import('./pages/refunds/simple-refund-contact/simple-refund-contact.component')
            .then((m) => m.SimpleRefundContactComponent),
      },
      {
        path: ecommerceConstants.urls.refunds[country.code] + '/' + ecommerceConstants.urls.cancel[country.code] + '/:channelOrderNumber/:productSku',
        loadComponent: () => import('./pages/refunds/refunds-info/refunds-info.component').then((m) => m.RefundsInfoComponent),
      },
      {
        path: ecommerceConstants.urls.refunds[country.code] + '/' + ecommerceConstants.urls.cancel[country.code] + '/' + ecommerceConstants.urls.compensation[country.code] + '/:channelOrderNumber/:productSku',
        loadComponent: () => import('./pages/refunds/super-late-compensation/super-late-compensation.component').then((m) => m.SuperLateCompensationComponent),
      },
      {
        path: ecommerceConstants.urls.refunds[country.code] + '/' + ecommerceConstants.urls.cancel[country.code] + '/' + ecommerceConstants.urls['voucher-success'][country.code],
        loadComponent: () => import('./pages/refunds/voucher-success/voucher-success.component').then((m) => m.VoucherSuccessComponent),
      },
      // Returns
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls.cancel[country.code] + '/' + ecommerceConstants.urls.contact[country.code] + '/:channelOrderNumber/:productSku',
        loadComponent: () =>
          import('./pages/returns/simple-return-contact/simple-return-contact.component')
            .then((m) => m.SimpleReturnContactComponent),
      },
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls.reason[country.code] + '/:channelOrderNumber/:productSku',
        loadComponent: () => import('./pages/returns/return-reason/return-reason.component').then((m) => m.ReturnReasonComponent),
      },
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls['waiting-for-compensation'][country.code] + '/:returnChannelOrderNumber',
        loadComponent: () => import('./pages/returns/return-waiting-for-compensation/return-waiting-for-compensation.component').then((m) => m.ReturnWaitingForCompensationComponent),
      },
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls['approved'][country.code] + '/:returnChannelOrderNumber',
        loadComponent: () => import('./pages/returns/return-approved/return-approved.component').then((m) => m.ReturnApprovedComponent),
      },
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls.product[country.code] + '/:returnChannelOrderNumber',
        loadComponent: () => import('./pages/returns/return-product-detail/return-product-detail.component').then((m) => m.ReturnProductDetailComponent),
      },
      {
        path: ecommerceConstants.urls.returns[country.code] + '/' + ecommerceConstants.urls.return[country.code] + '/:returnChannelOrderNumber',
        loadComponent: () => import('./pages/returns/return/return.component').then((m) => m.ReturnComponent),
      },
      // Services
      {
        path: ecommerceConstants.urls.services[country.code] + '/:service',
        loadComponent: () => import('./pages/services/service/service.component').then((m) => m.ServiceComponent),
      },
      {
        path: ecommerceConstants.urls.promotions[country.code] + '/:service',
        loadComponent: () => import('./pages/services/service/service.component').then((m) => m.ServiceComponent),
      },
      {
        path: ecommerceConstants.urls.sustainability[country.code],
        loadComponent: () =>
          import('./pages/glovo/sustainability/sustainability.component').then((m) => m.SustainabilityComponent),
      },
      {
        path: ecommerceConstants.urls.sustainability[country.code] + '/:item',
        loadComponent: () =>
          import('./pages/glovo/sustainability/sustainability.component').then((m) => m.SustainabilityComponent),
      },
      {
        path: ecommerceConstants.urls.landings[country.code] + '/:landing',
        loadComponent: () => import('./pages/landing/landing.component').then((m) => m.LandingComponent),
      },
      {
        path: ecommerceConstants.urls.support[country.code],
        loadComponent: () => import('./pages/support/support.component').then((m) => m.SupportComponent),
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls['contact-support'][country.code],
        loadComponent: () => import('./pages/contact-support/contact-support.component').then((m) => m.ContactSupportComponent),
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls.faqs[country.code],
        loadComponent: () => import('./pages/faqs/faqs.component').then((m) => m.FaqsComponent),
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls.faqs[country.code] + '/:faqClassificationSlug',
        loadComponent: () =>
          import('./pages/faqs/faqs-questions/faqs-questions.component').then((m) => m.FaqsQuestionsComponent),
        data: { hotjar: true },
      },
      {
        path: ecommerceConstants.urls.faqs[country.code] + '/:faqClassificationSlug' + '/:faqSlug',
        loadComponent: () =>
          import('./pages/faqs/faqs-answer/faqs-answer.component').then((m) => m.FaqsAnswerComponent),
        data: { hotjar: true },
      },
      // Posts
      {
        path: ecommerceConstants.urls.posts[country.code],
        loadComponent: () => import('./pages/posts/posts.component').then((m) => m.PostsComponent),
      },
      {
        path: ecommerceConstants.urls.posts[country.code] + '/:post',
        loadComponent: () => import('./pages/post/post.component').then((m) => m.PostComponent),
      },
      // Otros
      {
        path: 'error-404',
        loadComponent: () => import('./pages/static/error/error.component').then((m) => m.ErrorComponent),
      },
      {
        path: ecommerceConstants.urls['cookies-policy'][country.code],
        loadComponent: () =>
          import('./pages/static/cookies-policy/cookies-policy.component').then((m) => m.CookiesPolicyComponent),
      },
      // Landings
      {
        path: ecommerceConstants.urls['landing-courier'][country.code],
        loadComponent: () =>
          import('./pages/landing-courier/landing-courier.component').then((m) => m.LandingCourierComponent),
      },
      {
        path: ecommerceConstants.urls['landing-custom-products'][country.code],
        loadComponent: () =>
          import('./pages/landing-custom-products/landing-custom-products.component').then((m) => m.LandingCustomProductsComponent),
      },
    ],
    });
  });

  return countryRoutes;
}

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled'
    }),
  ],
  exports: [RouterModule],
  providers: [AuthGuard, NoAuthGuard],
})
export class AppRoutingModule {}
